/**
 * UNDO-TRASH-LIGHT (L2) — Papelera de recetas: recetas soft-borradas, con
 * restaurar y eliminar definitivamente. Local-first: opera sobre el SQLite de
 * la cuenta y sincroniza por el write-path normal (el servidor registra
 * restored/deleted). Borrar una receta desde la UI la trae aquí; restaurarla
 * hace que los slots del planner que la referencian vuelvan a resolverse.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { ArchiveRestore, ChefHat, Trash2 } from "lucide-solid";
import {
  deleteRecipeMutation,
  getTrashedRecipesQuery,
  queryKeys,
  restoreRecipeMutation,
  TRASH_PURGE_DAYS,
  trashRecipeMutation,
} from "@mealplan/shared";
import type { Recipe } from "@mealplan/powersync";
import { createPowerSyncQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { registerUndo } from "@/lib/undo";
import { undoMessages } from "@/lib/undo-messages";

function TrashSkeleton() {
  return (
    <div class="space-y-3" data-testid="trash-skeleton">
      <For each={[0, 1, 2]}>
        {() => (
          <div class="flex items-center gap-3 rounded-xl border border-border/20 px-4 py-3">
            <Skeleton class="h-9 w-9 animate-shimmer rounded-lg" />
            <div class="flex-1 space-y-1.5">
              <Skeleton class="h-3.5 w-1/2 animate-shimmer rounded" />
              <Skeleton class="h-3 w-1/4 animate-shimmer rounded" />
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

export function TrashView() {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const [confirmPurge, setConfirmPurge] = createSignal<{ id: string; name: string } | null>(null);
  // Nombre para el diálogo, resuelto FUERA del `t()` para no dejar un literal
  // vacío (`?? ""`) dentro de la llamada (dispara lingui/no-single-variables).
  const purgeName = (): string => confirmPurge()?.name ?? "";

  const trashedRecipes = createPowerSyncQuery<Recipe>({
    queryKey: () => queryKeys.recipes.trashed(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = userId();
      return dbInstance && uid ? getTrashedRecipesQuery(dbInstance, uid) : null;
    },
    enabled: () => Boolean(drizzle() && userId()),
  });

  const isLoading = () => trashedRecipes.isFetching && (trashedRecipes.data ?? []).length === 0;
  const isEmpty = createMemo(() => (trashedRecipes.data ?? []).length === 0);

  const MS_PER_DAY = 86_400_000;

  /** «borrado hace X» — tiempo relativo desde `deleted_at` (checklist de papelera). */
  const deletedAgoLabel = (iso: string | null): string => {
    if (!iso) {
      return "";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: "auto" });
    const diffDays = Math.round((date.getTime() - Date.now()) / MS_PER_DAY);
    if (diffDays === 0) {
      const diffHours = Math.round((date.getTime() - Date.now()) / 3_600_000);
      if (diffHours === 0) {
        return rtf.format(Math.round((date.getTime() - Date.now()) / 60_000), "minute");
      }
      return rtf.format(diffHours, "hour");
    }
    return rtf.format(diffDays, "day");
  };

  /**
   * «se eliminará definitivamente en Y días» — countdown = deleted_at +
   * TRASH_PURGE_DAYS − now. Devuelve los días restantes (mínimo 0) o null si no
   * hay fecha válida.
   */
  const daysUntilPurge = (iso: string | null): number | null => {
    if (!iso) {
      return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const purgeAt = date.getTime() + TRASH_PURGE_DAYS * MS_PER_DAY;
    return Math.max(0, Math.ceil((purgeAt - Date.now()) / MS_PER_DAY));
  };

  const restore = (recipeId: string): void => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    void (async () => {
      try {
        await restoreRecipeMutation(dbInstance, recipeId);
        // Restaurar es deshacible: la inversa vuelve a mandarla a la papelera.
        registerUndo(undoMessages(t, t(msg`Recipe restored`)), async () => {
          await trashRecipeMutation(dbInstance, recipeId);
        });
      } catch (err: unknown) {
        console.error("[trash] restore failed:", err);
        showToast(t(msg`Something went wrong`), "error");
      }
    })();
  };

  const purge = (recipeId: string): void => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    // Eliminar definitivamente NO tiene undo (irreversible a propósito).
    void (async () => {
      try {
        await deleteRecipeMutation(dbInstance, recipeId);
        showToast(t(msg`Recipe permanently deleted`), "success");
      } catch (err: unknown) {
        console.error("[trash] purge failed:", err);
        showToast(t(msg`Something went wrong`), "error");
      }
    })();
  };

  return (
    <div class="space-y-6" data-testid="trash-page">
      <PageHeader
        title={t(msg`Recipe trash`)}
        description={t(msg`Deleted recipes wait here — restore them or delete them forever.`)}
        backTo="/profile"
        icon={Trash2}
      />

      <Show when={!isLoading()} fallback={<TrashSkeleton />}>
        <Show
          when={!isEmpty()}
          fallback={
            <div data-testid="trash-empty">
              <EmptyState
                icon={Trash2}
                title={t(msg`Trash is empty`)}
                description={t(msg`Recipes you delete land here — nothing is lost right away.`)}
              />
            </div>
          }
        >
          <div class="space-y-2">
            <For each={trashedRecipes.data ?? []}>
              {(recipe) => (
                <div
                  class="flex items-center gap-3 rounded-xl border border-border/30 bg-card/60 px-4 py-3"
                  data-testid="trash-recipe"
                  data-trash-name={recipe.name ?? ""}
                >
                  <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <ChefHat class="h-4.5 w-4.5" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-medium">
                      {recipe.name ?? t(msg`Untitled recipe`)}
                    </p>
                    <Show when={recipe.deleted_at}>
                      <p class="text-xs text-muted-foreground">
                        {t(msg`Deleted {ago}`, { ago: deletedAgoLabel(recipe.deleted_at) })}
                      </p>
                    </Show>
                    <Show when={daysUntilPurge(recipe.deleted_at) !== null}>
                      <p
                        class="text-xs text-muted-foreground/80"
                        data-testid="trash-purge-countdown"
                      >
                        {t(msg`Will be permanently deleted in {days} days`, {
                          days: daysUntilPurge(recipe.deleted_at) ?? 0,
                        })}
                      </p>
                    </Show>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="trash-recipe-restore"
                    disabled={!drizzle()}
                    onClick={() => restore(recipe.id)}
                  >
                    <ArchiveRestore class="mr-1 h-4 w-4" />
                    {t(msg`Restore`)}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="text-destructive hover:text-destructive"
                    data-testid="trash-recipe-purge"
                    disabled={!drizzle()}
                    aria-label={t(msg`Delete forever`)}
                    onClick={() =>
                      setConfirmPurge({
                        id: recipe.id,
                        name: recipe.name ?? t(msg`Untitled recipe`),
                      })
                    }
                  >
                    <Trash2 class="h-4 w-4" />
                  </Button>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <ConfirmDialog
        open={confirmPurge() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmPurge(null);
          }
        }}
        title={t(msg`Delete forever?`)}
        description={t(
          msg`"{name}" and its ingredients will be permanently deleted. This cannot be undone.`,
          { name: purgeName() },
        )}
        variant="destructive"
        confirmLabel={t(msg`Delete forever`)}
        onConfirm={() => {
          const target = confirmPurge();
          setConfirmPurge(null);
          if (target) {
            purge(target.id);
          }
        }}
      />
    </div>
  );
}
